/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef } from 'react'

import debounce from 'debounce'

export const DEBOUNCE_TIME = 250

interface Props<T extends (...arguments_: Parameters<T>) => ReturnType<T> | undefined> {
    onChange: T
    debounceTime?: number
    flushOnUnmount?: boolean
}

const useDebounce = <T extends (...arguments_: Parameters<T>) => ReturnType<T> | undefined>(props: Props<T>) => {
    const { onChange, debounceTime, flushOnUnmount = false } = props

    const onChangeRef = useRef<T>(onChange)
    onChangeRef.current = onChange

    const debouncedFnRef = useRef<ReturnType<typeof debounce>>(null!)
    debouncedFnRef.current = useMemo(
        () => debounce((...args: any) => onChangeRef.current?.(...args), debounceTime ?? DEBOUNCE_TIME),
        [debounceTime],
    )

    useEffect(() => {
        return () => (flushOnUnmount ? debouncedFnRef.current.flush() : debouncedFnRef.current.clear())
    }, [flushOnUnmount])

    return useMemo(
        () => ({
            debounce: (...args: Parameters<T>) => debouncedFnRef.current(...(args as unknown[])),
            flush: () => debouncedFnRef.current.flush(),
            clear: () => debouncedFnRef.current.clear(),
        }),
        [],
    )
}

export default useDebounce
